const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Get token from localStorage
const getToken = () => localStorage.getItem('token');

const getHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token && { Authorization: `Bearer ${token}` })
});

// Helper to get proper cover art URL
export const getCoverArtUrl = (coverArtUrl, songId) => {
  if (!coverArtUrl) return null;
  // Return cover art URL as-is (external URLs, MongoDB GridFS URLs, etc.)
  return coverArtUrl;
};

export const api = {
  // Generic GET method
  get: async (path) => {
    const token = getToken();
    const res = await fetch(`${API}${path}`, { headers: getHeaders(token) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || `GET ${path} failed`);
    return data;
  },

  // Generic POST method
  post: async (path, body) => {
    const token = getToken();
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || `POST ${path} failed`);
    return data;
  },

  // Generic PUT method
  put: async (path, body) => {
    const token = getToken();
    const res = await fetch(`${API}${path}`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || `PUT ${path} failed`);
    return data;
  },

  // Generic file upload (FormData) — preserves auth, omits Content-Type so
  // the browser sets the multipart boundary itself.
  upload: async (path, formData) => {
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API}${path}`, { method: 'POST', headers, body: formData });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `Upload ${path} failed`);
    return data;
  },

  // Generic DELETE method
  delete: async (path) => {
    const token = getToken();
    const res = await fetch(`${API}${path}`, {
      method: 'DELETE',
      headers: getHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `DELETE ${path} failed`);
    return data;
  },

  // Auth
  login: async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    return data;
  },

  signup: async (email, password, role) => {
    const res = await fetch(`${API}/auth/signup`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email, password, role })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Signup failed');
    return data;
  },

  getMe: async (token) => {
    const res = await fetch(`${API}/auth/me`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get user');
    return res.json();
  },

  // Daily Quote
  getDailyQuote: async (token) => {
    const res = await fetch(`${API}/quotes/today`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get daily quote');
    return res.json();
  },

  // Artist Profile
  getArtistProfile: async (token) => {
    const res = await fetch(`${API}/artist/profile`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get profile');
    return res.json();
  },

  getArtistStats: async (token) => {
    const res = await fetch(`${API}/artist/stats`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get artist stats');
    return res.json();
  },

  getArtistSongsStats: async (token) => {
    const res = await fetch(`${API}/artist/songs-stats`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get song stats');
    return res.json();
  },

  updateArtistProfile: async (token, data) => {
    const res = await fetch(`${API}/artist/profile`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to update profile');
    }
    return res.json();
  },

  uploadArtistProfileImage: async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetch(`${API}/artist/profile/upload-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload profile image');
    }
    return res.json();
  },

  uploadExecutiveProfileImage: async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/executive/profile/upload-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload profile image');
    }
    return res.json();
  },

  updateArtistProfileImage: async (token, profileImageUrl) => {
    const params = new URLSearchParams({ profile_image_url: profileImageUrl });
    const res = await fetch(`${API}/artist/profile/image?${params}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to update profile image');
    }
    return res.json();
  },

  uploadCoverArt: async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetch(`${API}/songs/upload-cover-art`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload cover art');
    }
    return res.json();
  },

  // Executive Profile
  getExecutiveProfile: async (token) => {
    const res = await fetch(`${API}/executive/profile`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get profile');
    return res.json();
  },

  updateExecutiveProfile: async (token, data) => {
    const res = await fetch(`${API}/executive/profile`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update profile');
    return res.json();
  },

  // Executives Discovery
  listExecutives: async (token, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${API}/executives?${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to list executives');
    return res.json();
  },

  getExecutive: async (token, id) => {
    const res = await fetch(`${API}/executives/${id}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get executive');
    return res.json();
  },

  // Services
  listServices: async (token) => {
    const res = await fetch(`${API}/services`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to list services');
    return res.json();
  },

  // Songs
  listSongs: async (token) => {
    const res = await fetch(`${API}/songs`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to list songs');
    return res.json();
  },

  createSong: async (token, data) => {
    const res = await fetch(`${API}/songs`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create song');
    return res.json();
  },

  updateSong: async (token, id, data) => {
    const res = await fetch(`${API}/songs/${id}`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update song');
    return res.json();
  },

  deleteSong: async (token, id) => {
    const res = await fetch(`${API}/songs/${id}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to delete song');
    return res.json();
  },

  getSong: async (token, id) => {
    const res = await fetch(`${API}/songs/${id}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get song');
    return res.json();
  },

  updateSongAudio: async (token, songId, audioUrl, duration) => {
    const params = new URLSearchParams({ audio_url: audioUrl });
    if (duration) params.append('duration_seconds', duration);
    const res = await fetch(`${API}/songs/${songId}/audio?${params}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to update song audio');
    return res.json();
  },

  updateSongCoverArt: async (token, songId, coverArtUrl) => {
    const res = await fetch(`${API}/songs/${songId}/cover-art?cover_art_url=${encodeURIComponent(coverArtUrl)}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to update cover art');
    return res.json();
  },

  extractAudioMetadata: async (token, url) => {
    const res = await fetch(`${API}/audio/extract-metadata?url=${encodeURIComponent(url)}`, {
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to extract metadata');
    return res.json();
  },

  // Submissions
  createSubmission: async (token, data) => {
    const res = await fetch(`${API}/submissions`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to create submission');
    }
    return res.json();
  },

  // Legacy createRadioSubmission / createCuratorSubmission helpers were removed
  // in iter 202 — they targeted endpoints that never existed on the backend
  // (`/submissions/radio` and `/submissions/curator`). The live submit flow
  // goes through `/playlist-curator/submit` and `/radio-station/submit`
  // directly from DiscoverExecutives.js handleCuratorSubmit / handleRadioSubmit.

  getArtistSubmissions: async (token, status = null) => {
    const query = status ? `?status=${status}` : '';
    const res = await fetch(`${API}/submissions/artist${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get submissions');
    return res.json();
  },

  getExecutiveSubmissions: async (token, status = null) => {
    const query = status ? `?status=${status}` : '';
    const res = await fetch(`${API}/submissions/executive${query}`, { headers: getHeaders(token) });
    if (!res.ok) {
      // Include the HTTP status + response body so the caller (and the
      // exec staring at empty tabs) can actually see what went wrong.
      let detail = '';
      try { detail = (await res.json())?.detail || ''; } catch (_) { /* ignore */ }
      throw new Error(`Failed to get submissions (HTTP ${res.status}${detail ? ': ' + detail : ''})`);
    }
    return res.json();
  },

  getSubmission: async (token, id) => {
    const res = await fetch(`${API}/submissions/${id}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get submission');
    return res.json();
  },

  // Decisions
  createDecision: async (token, data) => {
    const res = await fetch(`${API}/decisions`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to create decision');
    }
    return responseData;
  },

  updateDecision: async (token, decisionId, rating, decisionType, notes = '') => {
    const params = new URLSearchParams({ rating, decision_type: decisionType });
    if (notes) params.append('notes', notes);
    const res = await fetch(`${API}/decisions/${decisionId}?${params}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to update decision');
    }
    return responseData;
  },

  // Evaluation API
  getEvaluationDetails: async (token, submissionId) => {
    const res = await fetch(`${API}/submissions/${submissionId}/evaluation`, { headers: getHeaders(token) });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to get evaluation details');
    }
    return responseData;
  },

  submitEvaluationResponse: async (token, submissionId, responses) => {
    const res = await fetch(`${API}/submissions/${submissionId}/evaluation`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ responses })
    });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to submit evaluation');
    }
    return responseData;
  },

  // Scout Ratings
  createScoutRating: async (token, data) => {
    const res = await fetch(`${API}/scout-ratings`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create rating');
    return res.json();
  },

  submitScoutRating: async (token, submissionId, rating) => {
    const res = await fetch(`${API}/scout-ratings`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ submission_id: submissionId, rating })
    });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to submit rating');
    }
    return responseData;
  },

  // Hit List
  getHitList: async (token) => {
    const res = await fetch(`${API}/hit-list`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get hit list');
    return res.json();
  },

  // Saved Songs
  getSavedSongs: async (token) => {
    const res = await fetch(`${API}/executive/saved-songs`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get saved songs');
    return res.json();
  },

  saveSong: async (token, songId, artistId, hitListId = null, notes = '') => {
    const params = new URLSearchParams({ song_id: songId, artist_id: artistId });
    if (hitListId) params.append('hit_list_id', hitListId);
    if (notes) params.append('notes', notes);
    
    const res = await fetch(`${API}/executive/saved-songs?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to save song');
    return res.json();
  },

  unsaveSong: async (token, songId) => {
    const res = await fetch(`${API}/executive/saved-songs/${songId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to unsave song');
    return res.json();
  },

  renewSavedSong: async (token, songId) => {
    const res = await fetch(`${API}/executive/saved-songs/${songId}/renew`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to renew saved song');
    return res.json();
  },

  // Saved Songs — Radio Station
  getSavedSongsAsRadio: async (token) => {
    const res = await fetch(`${API}/radio-station/saved-songs`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get saved songs');
    return res.json();
  },
  saveSongAsRadio: async (token, songId, artistId, hitListId = null, source = 'queue') => {
    const params = new URLSearchParams({ song_id: songId, artist_id: artistId, source });
    if (hitListId) params.append('hit_list_id', hitListId);
    const res = await fetch(`${API}/radio-station/saved-songs?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to save song');
    return res.json();
  },
  unsaveSongAsRadio: async (token, songId) => {
    const res = await fetch(`${API}/radio-station/saved-songs/${songId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to unsave song');
    return res.json();
  },
  checkSavedAsRadio: async (token, songId) => {
    const res = await fetch(`${API}/radio-station/saved-songs/check/${songId}`, {
      headers: getHeaders(token)
    });
    if (!res.ok) return { saved: false };
    return res.json();
  },

  // Saved Songs — Playlist Curator
  getSavedSongsAsCurator: async (token) => {
    const res = await fetch(`${API}/playlist-curator/saved-songs`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get saved songs');
    return res.json();
  },
  saveSongAsCurator: async (token, songId, artistId, hitListId = null, source = 'queue') => {
    const params = new URLSearchParams({ song_id: songId, artist_id: artistId, source });
    if (hitListId) params.append('hit_list_id', hitListId);
    const res = await fetch(`${API}/playlist-curator/saved-songs?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to save song');
    return res.json();
  },
  unsaveSongAsCurator: async (token, songId) => {
    const res = await fetch(`${API}/playlist-curator/saved-songs/${songId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to unsave song');
    return res.json();
  },
  checkSavedAsCurator: async (token, songId) => {
    const res = await fetch(`${API}/playlist-curator/saved-songs/check/${songId}`, {
      headers: getHeaders(token)
    });
    if (!res.ok) return { saved: false };
    return res.json();
  },

  // Song Play Tracking & Points
  trackSongPlay: async (token, songId, eventType, source = 'hit_list', durationSeconds = null) => {
    const params = new URLSearchParams({ event_type: eventType, source });
    if (durationSeconds) params.append('duration_seconds', durationSeconds);
    
    const res = await fetch(`${API}/songs/${songId}/track-play?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to track play');
    return res.json();
  },

  getSongStats: async (token, songId) => {
    const res = await fetch(`${API}/songs/${songId}/stats`, {
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to get song stats');
    return res.json();
  },

  // Secondary Ratings (for shared recipients and scouts)
  rateSong: async (token, songId, rating, comment = null) => {
    const params = new URLSearchParams({ rating: rating.toString() });
    if (comment) params.append('comment', comment);
    
    const res = await fetch(`${API}/songs/${songId}/rate?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to rate song');
    return res.json();
  },

  getSongRatings: async (token, songId) => {
    const res = await fetch(`${API}/songs/${songId}/ratings`, {
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to get ratings');
    return res.json();
  },

  // Hit List Charts
  getHitListCharts: async (token, limit = 50) => {
    const res = await fetch(`${API}/hit-list/charts?limit=${limit}`, {
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to get charts');
    return res.json();
  },

  getWeeklyChartSnapshot: async (token) => {
    const res = await fetch(`${API}/hit-list/charts/weekly-snapshot`, {
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to get weekly snapshot');
    return res.json();
  },

  // Sharing
  getExecutiveTeam: async (token) => {
    const res = await fetch(`${API}/executives/team`, {
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to get team');
    return res.json();
  },

  shareSong: async (token, songId, shareToId, message = '', source = 'saved_songs') => {
    const params = new URLSearchParams({ share_to_id: shareToId, source });
    if (message) params.append('message', message);
    
    const res = await fetch(`${API}/songs/${songId}/share?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to share song');
    return res.json();
  },

  // Email a song share to someone NOT on the platform (managers, collaborators).
  shareSongByEmail: async (token, songId, recipientEmail, recipientName = '', message = '', source = 'hit_list') => {
    const params = new URLSearchParams({ recipient_email: recipientEmail, source });
    if (recipientName) params.append('recipient_name', recipientName);
    if (message) params.append('message', message);
    const res = await fetch(`${API}/songs/${songId}/share-email?${params}`, {
      method: 'POST',
      headers: getHeaders(token),
    });
    if (!res.ok) throw new Error('Failed to email share');
    return res.json();
  },

  // Send a message to the artist about a specific song (DM thread).
  messageArtistAboutSong: async (token, songId, body) => {
    const params = new URLSearchParams({ body });
    const res = await fetch(`${API}/songs/${songId}/message-artist?${params}`, {
      method: 'POST',
      headers: getHeaders(token),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to send message');
    }
    return res.json();
  },

  checkSongSaved: async (token, songId) => {
    const res = await fetch(`${API}/executive/saved-songs/check/${songId}`, {
      headers: getHeaders(token)
    });
    if (!res.ok) return { is_saved: false };
    return res.json();
  },

  // Scouts
  inviteScout: async (token, email) => {
    const res = await fetch(`${API}/scouts/invite`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ email })
    });
    if (!res.ok) throw new Error('Failed to invite scout');
    return res.json();
  },

  getScoutInviteDetails: async (inviteToken) => {
    const res = await fetch(`${API}/scouts/invite-details/${inviteToken}`);
    if (!res.ok) throw new Error('Invalid or expired invitation');
    return res.json();
  },

  lookupScoutInviteCode: async (code, email) => {
    const res = await fetch(`${API}/scouts/lookup-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Invalid code or email mismatch');
    return data;
  },

  lookupClaimCode: async (code, email) => {
    const res = await fetch(`${API}/claim/lookup-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Invalid code or email mismatch');
    return data;
  },

  regenerateScoutInviteCode: async (token, scoutId) => {
    const res = await fetch(`${API}/scouts/${scoutId}/regenerate-code`, {
      method: 'POST',
      headers: getHeaders(token),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to regenerate code');
    }
    return res.json();
  },

  acceptScoutInvite: async (data) => {
    const res = await fetch(`${API}/scouts/accept`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to accept invite');
    }
    return responseData;
  },

  listScouts: async (token) => {
    const res = await fetch(`${API}/scouts`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to list scouts');
    return res.json();
  },

  revokeScout: async (token, scoutId) => {
    const res = await fetch(`${API}/scouts/${scoutId}/revoke`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to revoke scout');
    return res.json();
  },

  // Scout Permissions
  getScoutPermissions: async (token, scoutId) => {
    const res = await fetch(`${API}/scouts/${scoutId}/permissions`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get permissions');
    return res.json();
  },

  updateScoutPermissions: async (token, scoutId, permissions) => {
    const res = await fetch(`${API}/scouts/${scoutId}/permissions`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(permissions)
    });
    if (!res.ok) throw new Error('Failed to update permissions');
    return res.json();
  },

  // Scout Recommendations
  recommendSong: async (token, songId, note = '') => {
    const params = note ? `?note=${encodeURIComponent(note)}` : '';
    const res = await fetch(`${API}/scouts/recommend/${songId}${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to recommend song');
    }
    return data;
  },

  getScoutRecommendations: async (token, status = null) => {
    const query = status ? `?status=${status}` : '';
    const res = await fetch(`${API}/executive/scout-recommendations${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get recommendations');
    return res.json();
  },

  updateRecommendationStatus: async (token, recommendationId, status) => {
    const res = await fetch(`${API}/executive/scout-recommendations/${recommendationId}?status=${status}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to update recommendation');
    return res.json();
  },

  // Scout Team Messaging
  getScoutThreads: async (token) => {
    const res = await fetch(`${API}/scouts/messages`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get threads');
    return res.json();
  },

  getScoutThreadMessages: async (token, threadId) => {
    const res = await fetch(`${API}/scouts/messages/${threadId}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get messages');
    return res.json();
  },

  sendScoutMessage: async (token, recipientId, body) => {
    const res = await fetch(`${API}/scouts/messages`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ recipient_id: recipientId, body })
    });
    if (!res.ok) throw new Error('Failed to send message');
    return res.json();
  },

  // Scout's Shared Access
  getSharedSavedSongs: async (token) => {
    const res = await fetch(`${API}/scouts/shared-saved-songs`, { headers: getHeaders(token) });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to get saved songs');
    }
    return responseData;
  },

  // Scout Profile
  getScoutProfile: async (token) => {
    const res = await fetch(`${API}/scouts/profile`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get profile');
    return res.json();
  },

  updateScoutProfile: async (token, data) => {
    const res = await fetch(`${API}/scouts/profile`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update profile');
    return res.json();
  },

  // Scout Performance Dashboard
  getScoutPerformanceDashboard: async (token) => {
    const res = await fetch(`${API}/executive/scout-performance`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get scout performance');
    return res.json();
  },

  getIndividualScoutPerformance: async (token, scoutId) => {
    const res = await fetch(`${API}/executive/scout-performance/${scoutId}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get scout performance');
    return res.json();
  },

  // Executive Privileges Status
  getExecutivePrivilegesStatus: async (token) => {
    const res = await fetch(`${API}/executive/privileges-status`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get privileges status');
    return res.json();
  },

  // Messages
  listThreads: async (token) => {
    const res = await fetch(`${API}/threads`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to list threads');
    return res.json();
  },

  getThreadMessages: async (token, threadId) => {
    const res = await fetch(`${API}/threads/${threadId}/messages`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get messages');
    return res.json();
  },

  sendMessage: async (token, threadId, body) => {
    const res = await fetch(`${API}/messages`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ thread_id: threadId, body })
    });
    if (!res.ok) throw new Error('Failed to send message');
    return res.json();
  },

  // Notifications
  getNotifications: async (token, unreadOnly = false) => {
    const query = unreadOnly ? '?unread_only=true' : '';
    const res = await fetch(`${API}/notifications${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get notifications');
    return res.json();
  },

  markNotificationRead: async (token, id) => {
    const res = await fetch(`${API}/notifications/${id}/read`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to mark as read');
    return res.json();
  },

  markAllNotificationsRead: async (token) => {
    const res = await fetch(`${API}/notifications/read-all`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to mark all as read');
    return res.json();
  },

  clearAllNotifications: async (token) => {
    const res = await fetch(`${API}/notifications/clear-all`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to clear notifications');
    return res.json();
  },

  // Reports
  createReport: async (token, data) => {
    const res = await fetch(`${API}/reports`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create report');
    return res.json();
  },

  // Admin
  getAdminAnalytics: async (token) => {
    const res = await fetch(`${API}/admin/analytics`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get analytics');
    return res.json();
  },

  getAdminReports: async (token, status = null) => {
    const query = status ? `?status=${status}` : '';
    const res = await fetch(`${API}/admin/reports${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get reports');
    return res.json();
  },

  updateReport: async (token, id, status, notes = null) => {
    const params = new URLSearchParams({ status });
    if (notes) params.append('resolution_notes', notes);
    const res = await fetch(`${API}/admin/reports/${id}?${params}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to update report');
    return res.json();
  },

  suspendUser: async (token, userId) => {
    const res = await fetch(`${API}/admin/users/${userId}/suspend`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to suspend user');
    return res.json();
  },

  activateUser: async (token, userId) => {
    const res = await fetch(`${API}/admin/users/${userId}/activate`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to activate user');
    return res.json();
  },

  getAuditLog: async (token) => {
    const res = await fetch(`${API}/admin/audit-log`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get audit log');
    return res.json();
  },

  getNotificationSettings: async (token) => {
    const res = await fetch(`${API}/admin/notification-settings`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get settings');
    return res.json();
  },

  updateNotificationSettings: async (token, eventType, sendEmail, recipients = '') => {
    const params = new URLSearchParams({ send_email: sendEmail });
    if (recipients) params.append('admin_email_recipients', recipients);
    const res = await fetch(`${API}/admin/notification-settings/${eventType}?${params}`, {
      method: 'PUT',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to update settings');
    return res.json();
  },

  // Membership & Plans
  listPlans: async (token, roleType = null) => {
    const query = roleType ? `?role_type=${roleType}` : '';
    const res = await fetch(`${API}/plans${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to list plans');
    return res.json();
  },

  getPlan: async (token, planId) => {
    const res = await fetch(`${API}/plans/${planId}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get plan');
    return res.json();
  },

  getMyPlan: async (token) => {
    const res = await fetch(`${API}/my-plan`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get plan');
    return res.json();
  },

  subscribeToPlan: async (token, planId, billingInterval = 'monthly') => {
    const params = new URLSearchParams({ 
      plan_id: planId, 
      billing_interval: billingInterval,
      origin_url: window.location.origin
    });
    const res = await fetch(`${API}/subscribe?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData.detail || 'Failed to subscribe');
    }
    return responseData;
  },

  unsubscribe: async (token, reason = null) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const res = await fetch(`${API}/unsubscribe${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to unsubscribe');
    return res.json();
  },

  checkBenefit: async (token, benefitKey) => {
    const res = await fetch(`${API}/check-benefit/${benefitKey}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to check benefit');
    return res.json();
  },

  checkSubmissionLimit: async (token) => {
    const res = await fetch(`${API}/check-submission-limit`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to check limit');
    return res.json();
  },

  // Admin Plans
  getAdminPlans: async (token) => {
    const res = await fetch(`${API}/admin/plans`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get plans');
    return res.json();
  },

  createPlan: async (token, data) => {
    const res = await fetch(`${API}/admin/plans`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create plan');
    return res.json();
  },

  updatePlan: async (token, planId, data) => {
    const res = await fetch(`${API}/admin/plans/${planId}`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update plan');
    return res.json();
  },

  deletePlan: async (token, planId) => {
    const res = await fetch(`${API}/admin/plans/${planId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to delete plan');
    return res.json();
  },

  setPlanBenefit: async (token, planId, key, value, description = '') => {
    const res = await fetch(`${API}/admin/plans/${planId}/benefits`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ plan_id: planId, key, value, description })
    });
    if (!res.ok) throw new Error('Failed to set benefit');
    return res.json();
  },

  deletePlanBenefit: async (token, planId, benefitKey) => {
    const res = await fetch(`${API}/admin/plans/${planId}/benefits/${benefitKey}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to delete benefit');
    return res.json();
  },

  initializeDefaultPlans: async (token) => {
    const res = await fetch(`${API}/admin/plans/initialize-defaults`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to initialize plans');
    return res.json();
  },

  getAdminUserPlans: async (token, status = null) => {
    const query = status ? `?status=${status}` : '';
    const res = await fetch(`${API}/admin/user-plans${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get user plans');
    return res.json();
  },

  adminSubscribeUser: async (token, userId, planId, billingInterval = 'monthly') => {
    const params = new URLSearchParams({ plan_id: planId, billing_interval: billingInterval });
    const res = await fetch(`${API}/admin/user-plans/${userId}/subscribe?${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to subscribe user');
    return res.json();
  },

  adminCancelUserPlan: async (token, userPlanId, reason = null) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const res = await fetch(`${API}/admin/user-plans/${userPlanId}/cancel${params}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to cancel plan');
    return res.json();
  },

  // ==================== Content & CMS ====================
  
  // Public content slots
  getContentSlot: async (key) => {
    const res = await fetch(`${API}/v1/content/slots/${key}`);
    if (!res.ok) return { found: false, key, content: null };
    return res.json();
  },

  getContentSlots: async (keys = []) => {
    const query = keys.length ? `?keys=${keys.join(',')}` : '';
    const res = await fetch(`${API}/v1/content/slots${query}`);
    if (!res.ok) return { slots: {} };
    return res.json();
  },

  // Public ad placements
  getAdPlacement: async (key) => {
    const res = await fetch(`${API}/v1/ad-placements/${key}`);
    if (!res.ok) return { found: false, placement_key: key, ad: null };
    return res.json();
  },

  trackAdClick: async (placementId) => {
    const res = await fetch(`${API}/v1/ad-placements/${placementId}/click`, {
      method: 'POST'
    });
    return res.ok;
  },

  // Admin content blocks
  getAdminContentBlocks: async (token, key = null) => {
    const query = key ? `?key=${key}` : '';
    const res = await fetch(`${API}/admin/content-blocks${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get content blocks');
    return res.json();
  },

  createContentBlock: async (token, data) => {
    const res = await fetch(`${API}/admin/content-blocks`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create content block');
    return res.json();
  },

  updateContentBlock: async (token, blockId, data) => {
    const res = await fetch(`${API}/admin/content-blocks/${blockId}`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update content block');
    return res.json();
  },

  deleteContentBlock: async (token, blockId) => {
    const res = await fetch(`${API}/admin/content-blocks/${blockId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to delete content block');
    return res.json();
  },

  // Admin ad placements
  getAdminAdPlacements: async (token, placementKey = null) => {
    const query = placementKey ? `?placement_key=${placementKey}` : '';
    const res = await fetch(`${API}/admin/ad-placements${query}`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get ad placements');
    return res.json();
  },

  createAdPlacement: async (token, data) => {
    const res = await fetch(`${API}/admin/ad-placements`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create ad placement');
    return res.json();
  },

  updateAdPlacement: async (token, placementId, data) => {
    const res = await fetch(`${API}/admin/ad-placements/${placementId}`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update ad placement');
    return res.json();
  },

  deleteAdPlacement: async (token, placementId) => {
    const res = await fetch(`${API}/admin/ad-placements/${placementId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    if (!res.ok) throw new Error('Failed to delete ad placement');
    return res.json();
  },

  getContentSlotKeys: async (token) => {
    const res = await fetch(`${API}/admin/content-slot-keys`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get slot keys');
    return res.json();
  },

  getAdPlacementKeys: async (token) => {
    const res = await fetch(`${API}/admin/ad-placement-keys`, { headers: getHeaders(token) });
    if (!res.ok) throw new Error('Failed to get placement keys');
    return res.json();
  },

  // Audio - get signed URL for playback
  getSignedAudioUrl: async (token, audioUrl) => {
    const res = await fetch(`${API}/audio/signed-url?audio_url=${encodeURIComponent(audioUrl)}`, { 
      headers: getHeaders(token) 
    });
    if (!res.ok) {
      console.warn('Failed to get signed URL, using original');
      return { signed_url: audioUrl, expires_in: 0 };
    }
    return res.json();
  }
};
